import {
  ConversationsSearchResultMatch,
  ConversationsSearchResultResultsByUser,
} from '@mm/state/actions/LiveChatCRUDActions';
import { useActor } from '@xstate/react';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import Spinner from '../../../../../../common/components/Spinner';
import { LiveChatContext } from '../../../LiveChatContext';
import { LcSearchStates } from '../../../liveChatMachine';
import { ChannelIcon } from '../../ChannelIcon';
import { ProfileImage } from '../../ProfileImage/ProfileImage';
import './shared.pcss';

type Props = {
  selectedConversationsContactId: string | undefined;
  selectedContactsContactId: string | undefined;
  setSelectedContactsContactId: (id: string | undefined) => void;
  setSelectedConversationsContactId: (id: string | undefined) => void;
};

export const FoundConversations = (props: Props) => {
  const {
    setSelectedContactsContactId,
    selectedConversationsContactId,
    setSelectedConversationsContactId,
  } = props;

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const setSearchResultMatches = (matches: ConversationsSearchResultMatch[]) =>
    lcMachineSend({
      type: 'LC_CONTEXT_UPDATE',
      payload: {
        searchResultMatches: matches,
      },
    });

  const getSearchResultSession = (
    remUserId: string | undefined,
    sessionId: number,
    addingOlder: boolean,
    reset?: boolean,
  ) =>
    lcMachineSend({
      type: 'GET_SEARCH_RESULT_SESSION',
      payload: {
        remUserId,
        sessionId,
        addingOlder,
        reset,
      },
    });

  const { foundConversations: conversations } = lcMachineState.context;

  const isSearchingConversations = lcMachineState.matches({
    mainLogic: { active: { conversationSearch: LcSearchStates.searching } },
  });

  const [showMore, setShowMore] = useState(false);
  const [conversationsToShow, setConversationsToShow] = useState<
    ConversationsSearchResultResultsByUser[]
  >([]);

  useEffect(() => {
    if (conversations) {
      const moreThanFive = conversations.resultsByUser.length > 5;
      setShowMore(moreThanFive);
      setConversationsToShow(
        moreThanFive
          ? conversations.resultsByUser.slice(0, 5)
          : conversations.resultsByUser,
      );
    } else {
      setConversationsToShow([]);
      setSearchResultMatches([]);
      setShowMore(false);
    }
  }, [JSON.stringify(conversations)]);

  const onClickShowMore = () => {
    if (conversations) {
      setShowMore(false);
      setConversationsToShow(conversations.resultsByUser);
    }
  };

  const onConversationClick = async (sessionId: number, remUserId: string) => {
    const selectedContactMachine = remUserId
      ? lcMachineState.context.contactsRefs[remUserId]
      : undefined;

    setSelectedContactsContactId(undefined);
    setSelectedConversationsContactId(remUserId);

    lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: remUserId });
    getSearchResultSession(remUserId, sessionId, false, true);
    selectedContactMachine?.send({
      type: 'LC_CONTACT_UPDATE',
      payload: {
        selectedSessionId: sessionId,
      },
    });
    if (conversations) {
      const resultByUser = conversations.resultsByUser.find(
        r => r.remoteUserId === remUserId,
      );

      if (resultByUser) {
        setSearchResultMatches(resultByUser.matches);
      }
    }
  };

  const generateName = (contact: ConversationsSearchResultResultsByUser) => {
    if (contact.contactFullName && contact.username) {
      if (
        contact.contactFullName === 'Instagram Contact IG' &&
        contact.username
      ) {
        return `@${contact.username}`;
      }
      return `${contact.contactFullName} (@${contact.username})`;
    }
    if (contact.contactFullName) {
      return `${contact.contactFullName}`;
    }
    if (contact.username) {
      return `@${contact.username}`;
    }
  };

  return (
    <div>
      <div className="lc-found-contacts__title">Messages</div>
      <Spinner
        show={isSearchingConversations}
        size={15}
        position="relative"
        backgroundColor="transparent"
      />
      {!isSearchingConversations && conversationsToShow.length === 0 && (
        <div className="lc-found-contacts__empty">No messages found.</div>
      )}
      {!isSearchingConversations && conversationsToShow.length > 0 && (
        <>
          <div className="lc-search-results--container">
            {conversationsToShow.map((c, idx) => (
              <div
                key={`found-conversation-id-${c.remoteUserId}`}
                className={classnames(
                  'lc-search-result__container',
                  'lc-search-result__container--conversation',
                  {
                    'lc-search-result__container--selected':
                      c.remoteUserId === selectedConversationsContactId,
                  },
                )}
                onClick={() =>
                  onConversationClick(
                    c.matches[0].conversationSessionId,
                    c.remoteUserId,
                  )
                }
              >
                <ProfileImage
                  profilePictureUrl={c.profilePic}
                  altText="Profile Image"
                />
                <div>
                  <div className="lc-found-contact--name">
                    <ChannelIcon
                      className="lc-found-contact--channel"
                      remoteUserId={c.remoteUserId}
                    />
                    {generateName(c)}
                  </div>
                  <div className="lc-found-messages--count">
                    {`Matched ${c.matches.length} message${
                      c.matches.length === 1 ? '' : 's'
                    }`}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {!isSearchingConversations && conversations && showMore && (
            <div
              className="lc-search-result__more-link"
              onClick={onClickShowMore}
            >
              {`${conversations.resultsByUser.length - 5} more messages`}
            </div>
          )}
        </>
      )}
    </div>
  );
};
